import { useEffect, useState } from "react";
import { api, fmtDate, formatErr, canWrite, COMPANY_COLORS } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Allegati } from "@/components/Allegati";
import { stato } from "@/pages/Scadenzario";
import { Plus, Pencil, Trash2, Search, ShieldCheck, Building2, BadgeCheck, Database, UserRound, FolderOpen } from "lucide-react";
import { toast } from "sonner";

const CAT = [
  { value: "durc", label: "DURC", icon: ShieldCheck, et: ["Documento", "Altro"] },
  { value: "visura", label: "Visura camerale", icon: Building2, et: ["Documento", "Altro"] },
  { value: "albo", label: "Albo autotrasportatori", icon: BadgeCheck, et: ["Iscrizione", "Ricevuta di pagamento", "Altro"] },
  { value: "dati_azienda", label: "Dati aziendali", icon: Database, et: ["Documento", "Altro"] },
  { value: "legale_rappresentante", label: "Legale rappresentante", icon: UserRound, et: ["Carta d'identità", "Patente", "Codice fiscale", "Altro"] },
  { value: "vari", label: "Vari documenti", icon: FolderOpen, et: ["Documento", "Altro"] },
];

export default function Documenti() {
  const { company } = useCompany();
  const { user } = useAuth();
  const writable = canWrite(user, "movimenti");
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("durc");
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(null);
  const load = () => api.get("/documenti", { params: { company } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [company]); // eslint-disable-line

  const t = q.trim().toLowerCase();
  const shown = items.filter((d) => d.categoria === cat && (!t || `${d.titolo} ${d.note || ""}`.toLowerCase().includes(t)));
  const scad = (c) => items.filter((d) => d.categoria === c && d.data_scadenza && new Date(d.data_scadenza) - new Date() < 30 * 86400000).length;
  const del = async (d) => {
    if (!window.confirm(`Eliminare "${d.titolo}" con i suoi allegati?`)) return;
    await api.delete(`/documenti/${d.id}`); toast.success("Eliminato"); load();
  };
  const cur = CAT.find((c) => c.value === cat);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-black">Documenti azienda</h1>
          <div className="text-sm text-muted-foreground">Le scadenze inserite qui compaiono nello Scadenzario e nella Dashboard come promemoria.</div>
        </div>
        {writable && <Button size="sm" className="h-8 gap-1" onClick={() => setDlg({ categoria: cat })} data-testid="doc-new-button"><Plus className="w-4 h-4" />Nuovo documento</Button>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
        {CAT.map((c) => {
          const n = items.filter((d) => d.categoria === c.value).length;
          const w = scad(c.value);
          return (
            <button key={c.value} type="button" onClick={() => setCat(c.value)} data-testid={`doc-cat-${c.value}`}
                    className={`text-left p-3 rounded-md border transition-colors ${cat === c.value ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-muted"}`}>
              <c.icon className={`w-5 h-5 mb-1 ${cat === c.value ? "text-primary" : "text-muted-foreground"}`} />
              <div className="font-heading font-bold text-sm">{c.label}</div>
              <div className="text-xs text-muted-foreground">{n} documenti{w > 0 && <span className="text-red-600 ml-1">· {w} in scadenza</span>}</div>
            </button>
          );
        })}
      </div>

      <div className="relative w-72">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Cerca in ${cur.label}...`} className="h-8 pl-7" data-testid="doc-search" />
      </div>

      <Card className="rounded-md overflow-x-auto">
        <table className="autotrans-table w-full" data-testid="doc-table">
          <thead><tr><th>Documento</th><th>Azienda</th><th>Rilascio</th><th>Scadenza</th><th>Stato</th><th>Note</th><th>Allegati</th><th className="w-20"></th></tr></thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-muted-foreground">Nessun documento in {cur.label}</td></tr>}
            {shown.map((d) => {
              const s = d.data_scadenza && stato(d.data_scadenza);
              return (
                <tr key={d.id} data-testid={`doc-row-${d.id}`}>
                  <td className="font-medium">{d.titolo}</td>
                  <td><Badge style={{ backgroundColor: COMPANY_COLORS[d.company], color: "white" }} className="h-5">{d.company}</Badge></td>
                  <td className="font-mono text-xs">{fmtDate(d.data_rilascio) || "—"}</td>
                  <td className="font-mono text-xs">{fmtDate(d.data_scadenza) || "—"}</td>
                  <td>{s ? <Badge className={`${s.cls} text-white h-5 whitespace-nowrap`}>{s.txt}</Badge> : <span className="text-xs text-muted-foreground">senza scadenza</span>}</td>
                  <td className="text-xs text-muted-foreground max-w-xs truncate">{d.note}</td>
                  <td><Allegati coll="documenti" item={d} onChange={load} writable={writable} etichette={cur.et} testId={`doc-allegati-${d.id}`} /></td>
                  <td className="whitespace-nowrap">{writable && <>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setDlg(d)} data-testid={`doc-edit-${d.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => del(d)} data-testid={`doc-delete-${d.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
      <DocDialog d={dlg} company={company} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); load(); }} />
    </div>
  );
}

const Lbl = ({ l, children }) => <div><Label className="text-xs font-heading uppercase tracking-wider">{l}</Label><div className="mt-1">{children}</div></div>;

const DocDialog = ({ d, company, onClose, onSaved }) => {
  const [f, setF] = useState({});
  useEffect(() => { if (d) setF(d.id ? { ...d } : { ...d, company: company === "all" ? "golmar" : company }); }, [d]); // eslint-disable-line
  const s = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async () => {
    try {
      if (f.id) await api.patch(`/documenti/${f.id}`, f); else await api.post("/documenti", f);
      toast.success("Documento salvato"); onSaved();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const L = Lbl;
  return (
    <Dialog open={!!d} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{f.id ? "Modifica documento" : "Nuovo documento"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {!f.id && <L l="Azienda"><Select value={f.company} onValueChange={s("company")}>
            <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
            <SelectContent>{["golmar", "vitam", "rotta66", "fmg"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select></L>}
          <L l="Sezione"><Select value={f.categoria} onValueChange={s("categoria")}>
            <SelectTrigger className="h-8" data-testid="doc-field-categoria"><SelectValue /></SelectTrigger>
            <SelectContent>{CAT.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select></L>
          <div className="col-span-2"><L l="Titolo"><Input className="h-8" value={f.titolo || ""} onChange={s("titolo")} placeholder="es. DURC INPS/INAIL, Carta d'identità legale rappr." data-testid="doc-field-titolo" /></L></div>
          <L l="Data rilascio"><Input type="date" className="h-8" value={f.data_rilascio || ""} onChange={s("data_rilascio")} data-testid="doc-field-rilascio" /></L>
          <L l="Data scadenza"><Input type="date" className="h-8" value={f.data_scadenza || ""} onChange={s("data_scadenza")} data-testid="doc-field-scadenza" /></L>
          <div className="col-span-2"><L l="Note"><Input className="h-8" value={f.note || ""} onChange={s("note")} data-testid="doc-field-note" /></L></div>
        </div>
        <div className="text-xs text-muted-foreground">I PDF (documento, ricevuta di pagamento…) si allegano dalla graffetta nella tabella dopo aver salvato.</div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Annulla</Button><Button onClick={save} data-testid="doc-save-button">Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
